import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Grid,
  Paper,
  alpha,
  IconButton,
  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Palette as PaletteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useToast } from '../ToastProvider/ToastProvider';
import { COLOR_OPTIONS } from '../../DashboardSidebar/DashboardSidebar';
import moment from 'moment';

interface Schedule {
  scheduleId: string;
  name: string;
  favorite: boolean;
  color: string;
  shiftsCount: number;
  activeScheduleFrequency?: string;
  availabilityScheduleRequiredTime?: number;
  activeScheduleDate?: string;
  allowSwapWithoutApproval?: boolean;
  allowPickUpWithoutApproval?: boolean;
  showOtherStaff?: boolean;
}

interface EditScheduleModalProps {
  open: boolean;
  onClose: () => void;
  schedule: Schedule | null;
  onSave: (
    scheduleId: string,
    name: string,
    color: string,
    favorite: boolean,
    activeScheduleFrequency: string,
    availabilityScheduleRequiredTime: number,
    settings: {
      allowSwapWithoutApproval: boolean;
      allowPickUpWithoutApproval: boolean;
      showOtherStaff: boolean;
    }
  ) => Promise<void>;
  existingSchedules: Schedule[];
}

export default function EditScheduleModal({
  open,
  onClose,
  schedule,
  onSave,
  existingSchedules
}: EditScheduleModalProps) {
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: '',
    color: '#2196F3',
    favorite: false,
    activeScheduleFrequency: 'weekly',
    availabilityScheduleRequiredTime: 1,
    settings: {
      allowSwapWithoutApproval: false,
      allowPickUpWithoutApproval: false,
      showOtherStaff: true,
    }
  });

  const [originalName, setOriginalName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  // Load schedule data when modal opens
  useEffect(() => {
    if (open && schedule) {
      setFormData({
        name: schedule.name || '',
        color: schedule.color || '#2196F3',
        favorite: schedule.favorite || false,
        availabilityScheduleRequiredTime: schedule.availabilityScheduleRequiredTime || 1,
        activeScheduleFrequency: schedule.activeScheduleFrequency || 'weekly',
        settings: {
          allowSwapWithoutApproval: schedule.allowSwapWithoutApproval ?? false,
          allowPickUpWithoutApproval: schedule.allowPickUpWithoutApproval ?? false,
          showOtherStaff: schedule.showOtherStaff ?? true,
        }
      });
      setOriginalName(schedule.name || '');
    }
  }, [open, schedule]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleColorSelect = (color: string) => {
    setFormData({
      ...formData,
      color
    });
  };

  const toggleFavorite = () => {
    setFormData({
      ...formData,
      favorite: !formData.favorite
    });
  };

  const handleScheduleFrequency = (e: any) => {
    setFormData({
      ...formData,
      activeScheduleFrequency: e.target.value as string
    });
  };

  const handleAvailabilityScheduleRequiredTime = (e: any) => {
    setFormData({
      ...formData,
      availabilityScheduleRequiredTime: e.target.value as number
    });
  };

  const handleSettingChange = (setting: keyof typeof formData.settings) => {
    setFormData({
      ...formData,
      settings: {
        ...formData.settings,
        [setting]: !formData.settings[setting]
      }
    });
  };

  const hasChanges = () => {
    if (!schedule) return false;

    return (
      formData.name !== schedule.name ||
      formData.color !== schedule.color ||
      formData.favorite !== schedule.favorite ||
      formData.settings.allowSwapWithoutApproval !== (schedule.allowSwapWithoutApproval ?? false) ||
      formData.settings.allowPickUpWithoutApproval !== (schedule.allowPickUpWithoutApproval ?? false) ||
      formData.settings.showOtherStaff !== (schedule.showOtherStaff ?? true) ||
      formData.activeScheduleFrequency !== (schedule.activeScheduleFrequency || 'weekly') ||
      formData.availabilityScheduleRequiredTime !== (schedule.availabilityScheduleRequiredTime || 1)
    );
  };

  const validateForm = () => {
    // Check if name is empty
    if (!formData.name.trim()) {
      toast.showError('Schedule name is required');
      return false;
    }

    // Check if name is too long
    if (formData.name.length > 50) {
      toast.showError('Schedule name must be 50 characters or less');
      return false;
    }

    // Check if name is taken (if changed)
    if (formData.name !== originalName) {
      const nameTaken = existingSchedules.some(
        s => s.name.toLowerCase() === formData.name.trim().toLowerCase() && s.scheduleId !== schedule?.scheduleId
      );
      if (nameTaken) {
        toast.showError('A schedule with this name already exists');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!schedule) return;

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSave(
        schedule.scheduleId,
        formData.name.trim(),
        formData.color,
        formData.favorite,
        formData.activeScheduleFrequency,
        formData.availabilityScheduleRequiredTime,
        formData.settings
      );
      if (moment(schedule.activeScheduleDate).isAfter(moment())) {
        toast.showInfo('Note: Changes will take effect from the next active schedule date.');
      }
      toast.showSuccess('Schedule updated successfully');
      onClose();
    } catch (error) {
      toast.showError('Failed to update schedule. Please try again.');
      console.error('Error updating schedule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasChanges()) {
      // Could add unsaved changes warning here
    }
    onClose();
  };

  if (!schedule) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <EditIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Edit Schedule
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customize your schedule settings
            </Typography>
          </Box>
          <IconButton
            onClick={toggleFavorite}
            sx={{
              color: formData.favorite ? 'warning.main' : 'action.disabled',
              '&:hover': {
                bgcolor: alpha('#FFC107', 0.1)
              }
            }}
          >
            {formData.favorite ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>

          {/* Preview */}
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Preview
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(formData.color, 0.05),
                borderColor: formData.color,
                borderWidth: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: formData.color }}>
                  {formData.name.charAt(0).toUpperCase() || 'S'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.name || 'Schedule Name'}
                    </Typography>
                    {formData.favorite && (
                      <StarIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {schedule.shiftsCount || 0} shifts
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Schedule Name */}
          <TextField
            fullWidth
            label="Schedule Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Main Schedule, Weekend Shifts"
            required
            helperText={`${formData.name.length}/50 characters`}
            inputProps={{ maxLength: 50 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: formData.color
                }
              }
            }}
          />

          {/* Color Selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PaletteIcon color="action" />
              <Typography variant="body2" fontWeight={600}>
                Schedule Color
              </Typography>
            </Box>

            <Grid container spacing={1.5}>
              {COLOR_OPTIONS.map((colorOption) => (
                <Grid item xs={3} sm={2} key={colorOption.value}>
                  <Paper
                    onClick={() => handleColorSelect(colorOption.value)}
                    sx={{
                      aspectRatio: '1',
                      bgcolor: colorOption.value,
                      cursor: 'pointer',
                      border: 3,
                      borderColor: formData.color === colorOption.value ? 'grey.900' : 'transparent',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: 3
                      }
                    }}
                  >
                    {formData.color === colorOption.value && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: colorOption.value
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Selected Color Display */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: alpha(formData.color, 0.1),
                borderRadius: 1,
                border: 1,
                borderColor: alpha(formData.color, 0.3)
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Selected Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: formData.color,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {COLOR_OPTIONS.find(c => c.value === formData.color)?.name || 'Custom'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {formData.color}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Active Schedule Frequency */}
            <Paper elevation={0} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, flex: 1, marginRight: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Active Schedule Frequency
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The active schdeul frequency determines how often you will finalize your schedule to distribute to staff.
                      Although chnages can happen at any time, selecting a frequency helps keep your team on track.
                    </Typography>

                  </Box>
                </Box>
                <Box>
                  <Select
                    fullWidth
                    value={formData.activeScheduleFrequency}
                    onChange={handleScheduleFrequency}
                    size="small"
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Paper>
            <Divider />
            {/* Availability Schedule Required Time */}
            <Paper elevation={0} sx={{ p: 2, display: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, flex: 1, marginRight: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Availability Schedule Required Time
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Set the minimum amount of time staff need to provide their availability before the schedule is finalized.
                    </Typography>

                  </Box>
                </Box>
                <Box>
                  <Select
                    fullWidth
                    value={formData.availabilityScheduleRequiredTime}
                    onChange={handleAvailabilityScheduleRequiredTime}
                    size="small"
                  >
                    <MenuItem value={1}>1 Week</MenuItem>
                    <MenuItem disabled={formData.activeScheduleFrequency === "weekly"} value={2}>2 Weeks</MenuItem>
                  </Select>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Advanced Settings */}
          <Accordion
            expanded={advancedExpanded}
            onChange={(e, expanded) => setAdvancedExpanded(expanded)}
            sx={{
              border: 1,
              borderColor: 'divider',
              '&:before': { display: 'none' },
              boxShadow: 'none',
              mt: 2
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SettingsIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Advanced Settings
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Configure staff permissions and visibility
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Swap Without Approval */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>
                        <SwapHorizIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Allow Shift Swaps Without Approval
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Staff can swap shifts with each other instantly without requiring manager approval.
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={formData.settings.allowSwapWithoutApproval}
                      onChange={() => handleSettingChange('allowSwapWithoutApproval')}
                      color="primary"
                    />
                  </Box>
                </Paper>

                {/* Pickup Without Approval */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                      <Avatar sx={{ bgcolor: 'success.light', width: 40, height: 40 }}>
                        <AddIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Allow Pick Up Shifts Without Approval
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Staff can pick up open shifts immediately without waiting for manager approval.
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={formData.settings.allowPickUpWithoutApproval}
                      onChange={() => handleSettingChange('allowPickUpWithoutApproval')}
                      color="success"
                    />
                  </Box>
                </Paper>

                {/* Show Other Staff */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                      <Avatar sx={{ bgcolor: 'info.light', width: 40, height: 40 }}>
                        <VisibilityIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Show Other Staff on Shifts
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Staff can see who else is scheduled to work on each shift for better coordination.
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={formData.settings.showOtherStaff}
                      onChange={() => handleSettingChange('showOtherStaff')}
                      color="info"
                    />
                  </Box>
                </Paper>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim() || !hasChanges() || isSubmitting}
          sx={{
            bgcolor: "primary.main",
            '&:hover': {
              bgcolor: "primary.dark",
              filter: 'brightness(0.9)'
            }
          }}
        >
          {isSubmitting ? 'Saving...' : hasChanges() ? 'Save Changes' : 'No Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}